'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDiagonalEdgesProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDiagonalEdges - Diagonal grid pattern visible on edges, fading to center
 *
 * 45-degree crossed lines creating a diamond/argyle pattern
 * that is visible around the edges and fades out toward the center.
 *
 * Fixed fadeSize: 0.70
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDiagonalEdges />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDiagonalEdges({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDiagonalEdgesProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.7
  const size = `${fadeSize * 100}%`
  const stop1 = `${fadeSize * 100 * 0.5}%`
  const stop2 = `${fadeSize * 100 * 0.9}%`
  const stop3 = `${fadeSize * 100 * 1.2}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, transparent 0%, transparent ${stop1}, rgba(0,0,0,0.3) ${stop2}, rgba(0,0,0,0.6) ${stop3}, #000 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, color-mix(in srgb, var(--border), transparent 72%) 0px, color-mix(in srgb, var(--border), transparent 72%) 1px, transparent 1px, transparent 20px),
          repeating-linear-gradient(-45deg, color-mix(in srgb, var(--border), transparent 72%) 0px, color-mix(in srgb, var(--border), transparent 72%) 1px, transparent 1px, transparent 20px)
        `,
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
